import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { facturasApi } from '../api/facturas'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { descargarArchivo } from '../utils/descargar'
import { useStoredState } from '../hooks/useStoredState'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import Boton from '../components/Boton'
import { colors, font, text, space, radius } from '../design/tokens'
import Paginador from '../components/Paginador'

const FILTROS_DEFAULT = { q: '', fecha_desde: '', fecha_hasta: '' }

// Letra del comprobante (A/B/C) → color semántico del sistema. Cada letra
// reusa un token existente para mantener consistencia con el resto del UI.
const LETRA_TOKEN = {
  A: { bg: 'link' },   // azul = ResponInsc (mismo azul que t90/link)
  B: { bg: 'ok' },     // verde = ConsFinal/Monotributo
  C: { bg: 'warn' },   // ámbar = Exento
}

export default function FacturasPage() {
  const navigate = useNavigate()
  const [facturas, setFacturas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useStoredState('lgr.filtros.facturas', FILTROS_DEFAULT)
  const [paginacion, setPaginacion] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [page, setPage] = useState(1)

  const cargar = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true)
    try {
      const params = { page, ...Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)) }
      const { data } = await facturasApi.listar(params)
      setFacturas(data.data)
      setPaginacion({ current_page: data.current_page, last_page: data.last_page, total: data.total })
      setError(null)
    } catch {
      setError('No se pudieron cargar las facturas. Reintentá o limpiá los filtros.')
    } finally {
      if (!opts.silent) setLoading(false)
    }
  }, [filtros, page])

  useEffect(() => { cargar() }, [cargar])
  // Listado pasivo: refresh cada 30s en background para que aparezcan las
  // facturas nuevas (entran por chain Presea de Magento + modal) sin tener
  // que recargar a mano. Se pausa cuando la pestaña no está visible.
  useAutoRefresh(() => cargar({ silent: true }), 30000)

  const setFiltro = (k) => (e) => { setFiltros(f => ({ ...f, [k]: e.target.value })); setPage(1) }
  const limpiar = () => { setFiltros(FILTROS_DEFAULT); setPage(1) }

  const hayFiltros = Boolean(filtros.q || filtros.fecha_desde || filtros.fecha_hasta)

  const irAlPdf = (f) => {
    // Si Presea tiene el PDF descargado (URL externa), se abre directo
    // en otra pestaña. Si no, descargamos el PDF fiscal interno de LGR
    // vía `descargarArchivo` que mantiene el token Sanctum (window.open
    // contra /api/* tira RouteNotFoundException porque la pestaña nueva
    // no lleva el bearer).
    if (f.url) {
      window.open(f.url, '_blank', 'noopener')
      return
    }
    descargarArchivo(`/notas-venta/${f.nota_venta.id}/factura-fiscal`, `factura_${f.numero_presea}.pdf`)
  }

  return (
    <div>
      <div style={st.header}>
        <h2 style={st.title}>Facturas</h2>
      </div>

      <div style={st.filtros}>
        <input style={st.fi} placeholder="Buscar nº de factura, NV, cliente o CUIT…" value={filtros.q} onChange={setFiltro('q')} />
        <input style={st.fi} type="date" value={filtros.fecha_desde} onChange={setFiltro('fecha_desde')} />
        <input style={st.fi} type="date" value={filtros.fecha_hasta} onChange={setFiltro('fecha_hasta')} />
        <Boton variante={hayFiltros ? 'secondary' : 'ghost'} onClick={limpiar}>Limpiar</Boton>
      </div>

      {!loading && !error && (
        <p style={st.info}>
          {paginacion.total} factura{paginacion.total !== 1 ? 's' : ''}
          {hayFiltros && <strong style={st.filtrosActivos}> · filtros activos</strong>}
        </p>
      )}

      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead>
            <tr>
              {['Fecha', 'Letra', 'Nº Factura', 'Código', 'Cliente', 'NV', 'Importe', 'Acción'].map(h => (
                <th key={h} style={st.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={st.empty}>Cargando...</td></tr>
            ) : error ? (
              <tr><td colSpan={8} style={st.empty}>{error}</td></tr>
            ) : facturas.length === 0 ? (
              <tr><td colSpan={8} style={st.empty}>
                {hayFiltros ? (
                  <>
                    <div>No hay facturas que coincidan con los filtros.</div>
                    <Boton variante="secondary" sm style={{ marginTop: space.md }} onClick={limpiar}>Limpiar filtros</Boton>
                  </>
                ) : 'Sin resultados.'}
              </td></tr>
            ) : facturas.map(f => {
              const lt = LETRA_TOKEN[f.letra]
              const letraCol = lt
                ? { bg: colors[lt.bg + 'Bg'], color: colors[lt.bg] }
                : { bg: colors.borderSoft, color: colors.textSec }
              return (
                <tr
                  key={f.id}
                  style={st.tr}
                  onClick={() => navigate(`/notas-venta/${f.nota_venta.id}`)}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                  title="Ver detalle de la NV"
                >
                  <td style={st.tdFecha}>{fmtFecha(f.created_at)}</td>
                  <td style={st.td}>
                    {f.letra ? (
                      <span style={{ ...st.letraBadge, background: letraCol.bg, color: letraCol.color }}>{f.letra}</span>
                    ) : <span style={st.placeholder}>—</span>}
                  </td>
                  <td style={st.tdMono}>{f.numero_presea}</td>
                  <td style={st.tdMono}>
                    {f.cliente?.codigo_presea
                      ? f.cliente.codigo_presea
                      : <span style={st.chipPendiente}>Pendiente</span>}
                  </td>
                  <td style={st.td}>
                    <div style={st.principal}>{f.cliente?.nombre || '—'}</div>
                    {f.cliente?.cuit && <div style={st.subtituloMono}>CUIT {f.cliente.cuit}</div>}
                  </td>
                  <td style={st.tdSec}>
                    {f.nota_venta?.numero_lgr || '—'}
                  </td>
                  <td style={st.tdImporte}>
                    {f.importe != null
                      ? `$${fmtMonto(f.importe)}`
                      : <span style={st.placeholder}>—</span>}
                  </td>
                  <td style={st.tdAccion} onClick={e => e.stopPropagation()}>
                    <Boton
                      variante="secondary"
                      sm
                      onClick={() => irAlPdf(f)}
                      title={f.url ? 'Ver PDF de Presea' : 'Ver PDF fiscal LGR'}
                    >📄 PDF</Boton>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Paginador pagina={page} totalPaginas={paginacion.last_page} onCambiar={setPage} />
    </div>
  )
}

const st = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  filtros: { display: 'flex', gap: space.md, flexWrap: 'wrap', alignItems: 'center', marginBottom: space.md, background: colors.bg, padding: space.lg, borderRadius: radius.md, border: `1px solid ${colors.border}` },
  fi: { padding: '6px 12px', border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontSize: text.body, fontFamily: 'inherit', minWidth: 140, background: colors.bg },
  info: { margin: `0 0 ${space.md}px`, color: colors.textSec, fontSize: text.sec },
  filtrosActivos: { color: colors.link, fontWeight: 600 },

  tableWrap: { background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.border}`, overflow: 'auto', fontVariantNumeric: 'tabular-nums' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { padding: '8px 16px', textAlign: 'left', background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, fontWeight: 600, fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 48, cursor: 'pointer' },
  td: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, lineHeight: 1.25 },
  tdMono: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdFecha: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, whiteSpace: 'nowrap', lineHeight: 1.25 },
  tdSec: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, lineHeight: 1.25 },
  tdImporte: { padding: '8px 16px', verticalAlign: 'middle', fontSize: text.body, color: colors.text, fontFamily: font.mono, fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'right', lineHeight: 1.25 },
  tdAccion: { padding: '8px 16px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  principal: { fontSize: text.body, fontWeight: 500, color: colors.text },
  subtituloMono: { fontSize: text.meta, color: colors.textMeta, fontFamily: font.mono, marginTop: 2 },
  placeholder: { color: colors.textPlh, fontSize: text.sec },
  empty: { padding: 32, textAlign: 'center', color: colors.textMeta },

  letraBadge: { padding: '2px 8px', borderRadius: radius.sm, fontSize: text.meta, fontWeight: 600, fontFamily: font.mono, textTransform: 'uppercase' },
  // "Pendiente" usa el token semántico de warning (ámbar), igual que en
  // ClientesPage:181 → consistencia entre los lugares donde aparece la idea
  // de "Nº pendiente". El auditor `diseno` pidió unificar acá (19 jun 2026).
  chipPendiente: { background: colors.warnBg, color: colors.warn, padding: '2px 8px', borderRadius: radius.sm, fontSize: text.meta, fontWeight: 600 },

  paginacion: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: space.xl, marginTop: space.xl },
  pageBtn: { padding: '6px 14px', background: colors.bg, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: text.body, color: colors.text },
  pageInfo: { color: colors.textSec, fontSize: text.body },
}
